import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil
} from 'rxjs'
import {
  isOpenFinRuntime,
  MAIN_WINDOW_NAME
} from '../../containers/OpenFin/helpers'
import { getHub } from '../../helpers/hub'
import { createInvokeEpic } from '../../signalr/createInvokeEpic'
import { addLogItem } from '../log/actions'
import { setUserPreferences } from '../userPreferences/actions'
import { initialState as initialPreferences } from '../userPreferences/reducer'
import { forceReload, logError } from '../ws/actions'
import { loadWindowSettingsDefinition } from './definition'
import {
  actionTypesToTriggerSaving,
  defaultBackendSettings,
  settingsObject
} from './helpers'

const saveWindowSettingsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType('settings.handleWindowSettings'),
    switchMap(() => {
      return action$.pipe(
        ofType(...actionTypesToTriggerSaving),
        debounceTime(1500),
        switchMap((a) =>
          getHub()
            .invoke('SaveWindowSettings', settingsObject(state$))
            .pipe(
              map(() => addLogItem(`Saving settings because ${a.type}.`)),
              catchError((err) => of(logError(err)))
            )
        ),
        takeUntil(
          action$.pipe(ofType('settings.clearSettings', 'ws.force-reload'))
        )
      )
    })
  )

/*
 * Save immediately (not via the debounced epic above) so the server has the new
 * grid before the just-opened watchlist view fetches GetWindowSettings. createGrid
 * is state-synced, so gate to the main window — a child view's gridDefinitions can
 * be stale (e.g. setWatchListId isn't synced) and would clobber saved settings.
 */
const isMainWindow = () =>
  !isOpenFinRuntime() || fin.me.identity.name === MAIN_WINDOW_NAME

const saveOnCreateGridEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType('settings.handleWindowSettings'),
    switchMap(() =>
      action$.pipe(
        ofType('securities.createGrid'),
        filter(isMainWindow),
        switchMap((a) =>
          getHub()
            .invoke('SaveWindowSettings', settingsObject(state$))
            .pipe(
              map(() => addLogItem(`Saving settings because ${a.type}.`)),
              catchError((err) => of(logError(err)))
            )
        ),
        takeUntil(
          action$.pipe(ofType('settings.clearSettings', 'ws.force-reload'))
        )
      )
    )
  )

const clearSettingsEpic: Epic = (action$) =>
  action$.pipe(
    ofType('settings.clearSettings'),
    mergeMap(() =>
      getHub()
        .invoke('SaveWindowSettings', defaultBackendSettings)
        .pipe(
          mergeMap(() =>
            of(
              setUserPreferences(initialPreferences.userPreferences),
              addLogItem('clearing settings'),
              forceReload()
            )
          ),
          catchError((err) => of(logError(err)))
        )
    )
  )

export default combineEpics(
  clearSettingsEpic,
  createInvokeEpic(
    'settings.handleWindowSettings',
    loadWindowSettingsDefinition
  ),
  saveWindowSettingsEpic,
  saveOnCreateGridEpic
)
